import { findParentNodeClosestToPos, useEditorState } from "@tiptap/react";
import { isEqual } from "es-toolkit";

import { getBlockAlignment } from "@/components/tiptap/BlockAlign";
import { getBlockBackgroundColor } from "@/components/tiptap/BlockStyle";
import { latexInputPluginKey } from "@/components/tiptap/LatexInput";
import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";

import type { EditorState } from "@/models/EditorModel";
import type { FontSize, Color } from "@corca/data";
import type {
  CodeBlockAttrs,
  EmbedNodeAttrs,
  GraphNodeAttrs,
  PlotlyChartNodeAttrs,
} from "@corca/tiptap";
import type { Editor } from "@tiptap/react";

export function useEditorStateCommon({ editor }: { editor: Editor | null }) {
  return useEditorState({
    editor,
    selector: (): EditorState => {
      if (!editor) {
        return {};
      }

      const { selection } = editor.state;
      const { from, to } = selection || {};

      // selection.node behaves unreliably after undo - trying to locate node by position instead
      const node = editor.state.doc.nodeAt(from) ?? undefined;

      const lineCount = editor.state.doc.content.childCount - 1;

      const isDocumentTitle = editor.isActive("documentTitle");
      const isExpression = nodeIsExpression(node) && from !== to;

      const isImage = node?.type.name === "image";
      const isImageLoaded = isImage && !!editor?.getAttributes("image")?.key;
      const imageWidth = editor.getAttributes("image")?.width as number;
      const imageHeight = editor.getAttributes("image")?.height as number;

      const isGraph = editor.isActive("graph");
      const graphAttrs = isGraph ? (editor.getAttributes("graph") as GraphNodeAttrs) : null;

      const isEmbed = editor.isActive("embed");
      const embedAttrs = isEmbed ? (editor.getAttributes("embed") as EmbedNodeAttrs) : null;

      const isPlotlyChart = editor.isActive("plotlyChart");
      const plotlyChartAttrs = isPlotlyChart
        ? (editor.getAttributes("plotlyChart") as PlotlyChartNodeAttrs)
        : null;

      const isH1 = editor.isActive("heading", { level: 1 });
      const isH2 = editor.isActive("heading", { level: 2 });
      const isH3 = editor.isActive("heading", { level: 3 });
      const isHeading = editor.isActive("heading");

      const isInlineCode = editor.isActive("code");
      const isCodeBlock = editor.isActive("codeBlock");
      const codeBlockAttrs = isCodeBlock
        ? (editor.getAttributes("codeBlock") as CodeBlockAttrs)
        : null;

      const isBulletList = editor.isActive("bulletList");
      const isOrderedList = editor.isActive("orderedList");
      const isTaskList = editor.isActive("taskList");
      const isList = isBulletList || isOrderedList || isTaskList;
      const isBlockquote = editor.isActive("blockquote");
      const isCallout = editor.isActive("callout");
      const isHorizontalRule = editor.isActive("horizontalRule");
      const isParagraphNode = editor.isActive("paragraph");
      const isParagraph = isParagraphNode && !isList && !isHeading && !isBlockquote && !isCallout;
      const isCode = isInlineCode || isCodeBlock;
      const isText =
        !isImage && !isCodeBlock && !isHorizontalRule && !isGraph && !isEmbed && !isPlotlyChart;
      const blockAlign = getBlockAlignment(editor);
      const blockBackgroundColor = getBlockBackgroundColor(editor);
      const hasMathIndentation = editor.isActive({ mathIndentation: true });
      const latexInputState = latexInputPluginKey.getState(editor.state);
      const latexInput = latexInputState?.activeInput ?? null;
      const isTable = editor.isActive("table");
      const isTableCell = editor.isActive("tableCell");
      const isTableHeader = editor.isActive("tableHeader");
      const isTableCellLike = isTableCell || isTableHeader;
      const isNonConvertableBlock =
        isHorizontalRule ||
        isImage ||
        isDocumentTitle ||
        isTable ||
        isCodeBlock ||
        isEmbed ||
        isPlotlyChart;

      const tableCellNode = isTableCell
        ? findParentNodeClosestToPos(
            editor.state.selection.$from,
            (n) => n.type.name === "tableCell",
          )
        : undefined;

      const tableHeaderNode = isTableHeader
        ? findParentNodeClosestToPos(
            editor.state.selection.$from,
            (n) => n.type.name === "tableHeader",
          )
        : undefined;

      let textSize = editor.getAttributes("textStyle")?.size as FontSize;
      if (!textSize) {
        if (isDocumentTitle) {
          textSize = "40";
        } else if (isH1) {
          textSize = "30";
        } else if (isH2) {
          textSize = "24";
        } else if (isH3) {
          textSize = "20";
        } else if (tableCellNode) {
          textSize = (tableCellNode.node.attrs.size as FontSize) || "16";
        } else if (tableHeaderNode) {
          textSize = (tableHeaderNode.node.attrs.size as FontSize) || "16";
        } else {
          textSize = "16";
        }
      }

      let textColor = editor.getAttributes("textStyle")?.color as Color;
      if (!textColor) {
        if (tableCellNode) {
          textColor = tableCellNode.node.attrs.color as Color;
        } else if (tableHeaderNode) {
          textColor = tableHeaderNode.node.attrs.color as Color;
        }
      }

      const result: Required<EditorState> = {
        isDocumentTitle,
        isBold: editor.isActive("bold"),
        isItalic: editor.isActive("italic"),
        isUnderline: editor.isActive("underline"),
        isSubscript: editor.isActive("subscript"),
        isSuperscript: editor.isActive("superscript"),
        isInlineCode,
        isCodeBlock,
        isCode,
        isLink: editor.isActive("link"),
        linkHref: editor.getAttributes("link")?.href as string,
        isTextSelected: from !== to,
        isParagraph,
        isParagraphNode,
        isHeading,
        isH1,
        isH2,
        isH3,
        isList,
        isBulletList,
        isOrderedList,
        isTaskList,
        isBlockquote,
        isCallout,
        isNonConvertableBlock,
        isStrikethrough: editor.isActive("strike"),
        isExpression,
        isText,
        isHorizontalRule,
        isImage,
        isEmbed,
        embedAttrs,
        isTable,
        isTableCell,
        isTableHeader,
        isTableCellLike,
        isImageLoaded,
        imageWidth,
        imageHeight,
        textColor,
        textSize,
        blockAlign,
        blockBackgroundColor,
        hasMathIndentation,
        lineCount,
        isGraph,
        graphAttrs,
        isPlotlyChart,
        plotlyChartAttrs,
        codeBlockAttrs,
        latexInput,
      };
      return result;
    },
    equalityFn: isEqual,
  });
}
